import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

export default function TeacherPinDialog({ open, pin, onPinChange, onClose, onSubmit, busy, error }) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setFocused(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      aria-labelledby="teacher-pin-dialog-title"
      maxWidth={false}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      BackdropProps={{ sx: { bgcolor: 'rgba(18, 21, 43, 0.62)' } }}
      PaperProps={{
        sx: {
          width: 'min(340px, calc(100vw - 32px))',
          m: 2,
          borderRadius: '12px',
          background: 'linear-gradient(145deg, #fff 0%, #fbfaff 100%)',
          boxShadow: '0 18px 55px rgba(17, 20, 54, 0.28)',
          overflow: 'visible',
        },
      }}
    >
      <Box component="form" autoComplete="off" onSubmit={onSubmit} sx={{ position: 'relative', px: 3, pt: 2.2, pb: 2.5, textAlign: 'center' }}>
        <IconButton
          aria-label="PIN penceresini kapat"
          onClick={onClose}
          disabled={busy}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 7, minWidth: 44, minHeight: 44, color: '#665078', '&:hover': { bgcolor: '#f0edfa' }, '&:focus-visible': { outline: '3px solid #5128bc' } }}
        >
          <CloseRoundedIcon sx={{ fontSize: 19 }} />
        </IconButton>

        <Box sx={{ width: 56, height: 56, mx: 'auto', mb: 1.2, borderRadius: '50%', bgcolor: '#eee7ff', display: 'grid', placeItems: 'center' }}>
          <Box sx={{ width: 34, height: 34, borderRadius: '11px', background: 'linear-gradient(145deg, #8c55fa, #5a2de0)', display: 'grid', placeItems: 'center', boxShadow: '0 4px 10px rgba(92, 48, 210, .25)' }}>
            <LockRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
        </Box>

        <Typography id="teacher-pin-dialog-title" sx={{ fontSize: 19, lineHeight: 1.2, fontWeight: 900, color: '#151b44' }}>
          Öğretmen Modu
        </Typography>
        <Typography sx={{ mt: 0.45, fontSize: 13, color: '#576487' }}>4 haneli PIN'inizi girin.</Typography>

        <Box sx={{ position: 'relative', width: 'fit-content', mx: 'auto', mt: 2.2, mb: 2.2 }}>
          <Box sx={{ display: 'flex', gap: 1 }} aria-hidden="true">
            {Array.from({ length: 4 }, (_, index) => (
              <Box key={index} sx={{
                width: 43, height: 43, borderRadius: '8px', display: 'grid', placeItems: 'center',
                bgcolor: '#fdfdff', border: '1px solid',
                borderColor: focused && index === pin.length ? '#6a3ce6' : '#dce2f0',
                boxShadow: focused && index === pin.length ? '0 0 0 2px rgba(106, 60, 230, .12)' : '0 2px 5px rgba(31, 41, 74, .04)',
                color: '#11194b', fontSize: 23, fontWeight: 900, lineHeight: 1,
              }}>
                {pin[index] ? '•' : ''}
              </Box>
            ))}
          </Box>
          <Box
            component="input"
            ref={inputRef}
            autoFocus
            aria-label="Öğretmen PIN'i"
            // The visible slots mask the PIN; a password input triggers Chrome's save prompt even with autocomplete off.
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            maxLength={4}
            pattern="[0-9]{4}"
            value={pin}
            onChange={(event) => onPinChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'text', zIndex: 1, fontSize: 16 }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5, textAlign: 'left', fontSize: 12 }}>{error}</Alert>}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={pin.length !== 4 || busy}
          sx={{ height: 44, minHeight: 44, borderRadius: '9px', fontSize: 13, fontWeight: 800, textTransform: 'none',
            background: 'linear-gradient(90deg, #743de5, #6135df)', boxShadow: '0 5px 11px rgba(93, 49, 207, .22)',
            '&:hover': { background: 'linear-gradient(90deg, #6330d6, #5128c6)' },
            '&.Mui-disabled': { bgcolor: '#c4b4e8', color: '#fff', background: 'none', boxShadow: 'none' },
          }}
        >
          Giriş Yap
        </Button>
      </Box>
    </Dialog>
  );
}
